import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Search, BookOpen, Edit2, RotateCcw, Calendar } from 'lucide-react';
import { useData } from '../context/DataContext';
import { translations } from '../lib/i18n';
import type { Department, Faculty, Lesson } from '../lib/types';
import { LessonModeModal } from './LessonModeModal';
import { fetchSubjectYears } from '../lib/supabase';
import {
  getSavedWizard,
  saveWizardSelection,
  getSavedYear,
  saveSelectedYear,
  getSavedWilaya,
  saveSelectedWilaya,
} from '../lib/storage';

export const Wizard: React.FC = () => {
  const {
    faculties,
    subjects,
    lessons,
    wilayas,
    loading,
    error,
    retryLoad,
    language,
    getLessonCount,
    getSubjectCount,
    getYearsForFaculty,
  } = useData();

  const t = translations[language];
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // URL state
  const paramWilaya = searchParams.get('w');
  const paramFaculty = searchParams.get('f');
  const paramYear = searchParams.get('y') as Department | null;
  const paramSubject = searchParams.get('s');

  // Search filter for Wilayas step
  const [wilayaSearch, setWilayaSearch] = useState('');

  // Lesson mode modal selection
  const [modalLesson, setModalLesson] = useState<Lesson | null>(null);

  // Available exam years with real questions for current subject/wilaya
  const [availableYears, setAvailableYears] = useState<string[]>([]);
  const [loadingYears, setLoadingYears] = useState<boolean>(false);

  // Pre-fill from localStorage on initial visit if URL has no parameters
  useEffect(() => {
    if (!loading && faculties.length > 0) {
      if (!paramWilaya && !paramFaculty && !paramYear && !paramSubject) {
        const saved = getSavedWizard();
        if (saved.wilaya) {
          const nextParams: Record<string, string> = { w: saved.wilaya };
          if (saved.facultySlug) nextParams.f = saved.facultySlug;
          if (saved.year) nextParams.y = saved.year;
          if (saved.subjectId) nextParams.s = saved.subjectId;
          setSearchParams(nextParams, { replace: true });
        }
      }
    }
  }, [loading, faculties, paramWilaya, paramFaculty, paramYear, paramSubject, setSearchParams]);

  // Selected faculty resolution
  const selectedFaculty = useMemo<Faculty | null>(() => {
    if (!faculties.length) return null;
    if (paramFaculty) {
      const match = faculties.find((f) => f.slug === paramFaculty);
      if (match) return match;
    }
    // Default to first faculty if only 1 faculty exists
    if (faculties.length === 1) {
      return faculties[0];
    }
    return null;
  }, [faculties, paramFaculty]);

  // Selected subject resolution
  const selectedSubject = useMemo(() => {
    if (!paramSubject) return null;
    return subjects.find((s) => s.id === paramSubject) || null;
  }, [subjects, paramSubject]);

  // Save selection whenever it changes
  useEffect(() => {
    if (paramWilaya) {
      saveWizardSelection({
        wilaya: paramWilaya,
        facultySlug: selectedFaculty?.slug,
        year: paramYear || undefined,
        subjectId: paramSubject || undefined,
      });
    }
  }, [paramWilaya, selectedFaculty, paramYear, paramSubject]);

  // Determine current active step: 1: Wilaya, 2: Level, 3: Subject, 4: Lesson
  const currentStep = useMemo<number>(() => {
    if (!paramWilaya) return 1;
    if (!selectedFaculty || !paramYear) return 2;
    if (!paramSubject) return 3;
    return 4;
  }, [paramWilaya, selectedFaculty, paramYear, paramSubject]);

  // Accent-insensitive Wilaya search
  const filteredWilayas = useMemo(() => {
    const q = wilayaSearch.trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    if (!q) return wilayas;

    return wilayas.filter((w) => {
      if (w.state === 'all') {
        const allText = t.allWilayas.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
        return allText.includes(q);
      }
      const stateText = w.state.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
      return stateText.includes(q);
    });
  }, [wilayas, wilayaSearch, t.allWilayas]);

  // Subjects for the chosen faculty and year
  const filteredSubjects = useMemo(() => {
    if (!selectedFaculty || !paramYear) return [];
    return subjects
      .filter((s) => s.faculty_id === selectedFaculty.id && s.department === paramYear)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [subjects, selectedFaculty, paramYear]);

  // Lessons for the chosen subject
  const subjectLessons = useMemo(() => {
    if (!selectedSubject) return [];
    return lessons
      .filter((l) => l.subject_id === selectedSubject.id)
      .sort((a, b) => a.sort_order - b.sort_order);
  }, [lessons, selectedSubject]);

  // Fetch available exam years (e.g. 2025, 2024...) that actually have questions in this subject
  useEffect(() => {
    let isCurrent = true;
    if (currentStep !== 4 || !selectedSubject) {
      setAvailableYears([]);
      return;
    }

    const lessonIds = subjectLessons.map((l) => l.id);
    setLoadingYears(true);

    fetchSubjectYears({
      lessonIds,
      subjectName: selectedSubject.name,
      wilaya: paramWilaya || 'all',
    })
      .then((years) => {
        if (isCurrent) {
          setAvailableYears(years);
          setLoadingYears(false);
        }
      })
      .catch((err) => {
        console.error('Error fetching subject years:', err);
        if (isCurrent) {
          setLoadingYears(false);
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [currentStep, selectedSubject, paramWilaya, subjectLessons]);

  // Navigation step handlers
  const handleSelectWilaya = (state: string) => {
    saveSelectedWilaya(state);
    const savedYear = paramYear || getSavedYear() || 'fourth';
    const next: Record<string, string> = { w: state };
    if (faculties.length === 1) {
      next.f = faculties[0].slug;
    }
    next.y = savedYear;
    setSearchParams(next);
  };

  const handleSelectLevel = (faculty: Faculty, year: Department) => {
    saveSelectedYear(year);
    setSearchParams({
      w: paramWilaya || getSavedWilaya(),
      f: faculty.slug,
      y: year,
    });
  };

  const handleSelectSubject = (subjectId: string) => {
    const activeYear = paramYear || getSavedYear() || 'fourth';
    setSearchParams({
      w: paramWilaya || getSavedWilaya(),
      f: selectedFaculty?.slug || '',
      y: activeYear,
      s: subjectId,
    });
  };

  const handleOpenQuiz = (lessonId: string, openTheory = false) => {
    const wilayaParam = paramWilaya || getSavedWilaya();
    let url = `/quiz/${lessonId}?w=${encodeURIComponent(wilayaParam)}`;
    if (lessonId === 'all' && selectedSubject) {
      url += `&s=${encodeURIComponent(selectedSubject.id)}`;
    }
    if (openTheory) {
      url += `&theory=1`;
    }
    navigate(url);
  };

  // Chip back jumps
  const handleJumpToStep = (step: number) => {
    const activeYear = paramYear || getSavedYear() || 'fourth';
    if (step === 1) {
      setSearchParams({});
    } else if (step === 2) {
      setSearchParams({ w: paramWilaya || getSavedWilaya() });
    } else if (step === 3) {
      setSearchParams({
        w: paramWilaya || getSavedWilaya(),
        f: selectedFaculty?.slug || '',
        y: activeYear,
      });
    }
  };

  if (loading) {
    return (
      <main className="wizard-page">
        <div className="state-box">
          <p className="state-title">{t.loadingData}</p>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="wizard-page">
        <div className="state-box">
          <h2 className="state-title">{t.loadingErrorTitle}</h2>
          <p className="state-desc">{t.loadingErrorDesc}</p>
          <button type="button" className="btn-primary" onClick={retryLoad}>
            <RotateCcw size={16} /> {t.retry}
          </button>
        </div>
      </main>
    );
  }

  // Summary chip labels
  const wilayaChipLabel = paramWilaya === 'all' ? t.allWilayas : paramWilaya;
  const facultyName =
    language === 'ar'
      ? selectedFaculty?.name_ar
      : language === 'en'
      ? selectedFaculty?.name_en
      : selectedFaculty?.name_fr;
  const yearName = paramYear ? t.years[paramYear] : '';

  return (
    <main className="wizard-page">
      {/* SELECTION SUMMARY CHIPS ROW */}
      {paramWilaya && (
        <nav className="selection-chips-row" aria-label="Current path">
          {/* Step 1 Chip: Wilaya */}
          <button
            type="button"
            className="summary-chip"
            onClick={() => handleJumpToStep(1)}
            title="Edit Wilaya"
          >
            <span>{wilayaChipLabel}</span>
            <Edit2 size={13} className="chip-edit-icon" />
          </button>

          {/* Step 2 Chip: Faculty & Year */}
          {selectedFaculty && paramYear && (
            <button
              type="button"
              className="summary-chip"
              onClick={() => handleJumpToStep(2)}
              title="Edit Level"
            >
              <span>
                {facultyName} · {yearName}
              </span>
              <Edit2 size={13} className="chip-edit-icon" />
            </button>
          )}

          {/* Step 3 Chip: Subject */}
          {selectedSubject && (
            <button
              type="button"
              className="summary-chip"
              onClick={() => handleJumpToStep(3)}
              title="Edit Subject"
            >
              <span>{selectedSubject.name}</span>
              <Edit2 size={13} className="chip-edit-icon" />
            </button>
          )}
        </nav>
      )}

      {/* =======================================================
          STEP 1: WILAYA
          ======================================================= */}
      {currentStep === 1 && (
        <section aria-labelledby="step-1-title">
          <div className="step-header">
            <p className="step-indicator">1 / 4</p>
            <h1 id="step-1-title" className="step-title">
              {t.selectWilaya}
            </h1>
            <p className="step-desc">{t.selectWilayaDesc}</p>
          </div>

          {/* Instant Search Bar */}
          <div className="search-wrapper">
            <Search size={18} className="search-icon-left" aria-hidden="true" />
            <input
              type="search"
              className="search-input"
              placeholder={t.searchWilayaPlaceholder}
              value={wilayaSearch}
              onChange={(e) => setWilayaSearch(e.target.value)}
              aria-label={t.searchWilayaPlaceholder}
            />
          </div>

          <div className="cards-grid">
            {filteredWilayas.map((item) => {
              const isAll = item.state === 'all';
              const label = isAll ? t.allWilayas : item.state;
              const isDisabled = item.count === 0;

              return (
                <button
                  key={item.state}
                  type="button"
                  className="wizard-card"
                  disabled={isDisabled}
                  onClick={() => handleSelectWilaya(item.state)}
                >
                  <div className="card-top">
                    <div>
                      <h2 className="card-main-title">{label}</h2>
                      <p className="card-sub-info">
                        {item.count} {item.count === 1 ? t.questionSingular : t.questionsCount}
                      </p>
                    </div>
                    <span className="card-badge">
                      {isAll ? t.allBadge : item.count}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* =======================================================
          STEP 2: LEVEL (FACULTY + YEAR)
          ======================================================= */}
      {currentStep === 2 && (
        <section aria-labelledby="step-2-title">
          <div className="step-header">
            <p className="step-indicator">2 / 4</p>
            <h1 id="step-2-title" className="step-title">
              {t.selectLevel}
            </h1>
            <p className="step-desc">{t.selectLevelDesc}</p>
          </div>

          {/* If multiple faculties, show pill selector */}
          {faculties.length > 1 && (
            <div className="faculty-pills-row" role="tablist" aria-label={t.facultyLabel}>
              {faculties.map((f) => {
                const isSelected = (selectedFaculty?.id || faculties[0]?.id) === f.id;
                const fName =
                  language === 'ar' ? f.name_ar : language === 'en' ? f.name_en : f.name_fr;
                return (
                  <button
                    key={f.id}
                    type="button"
                    role="tab"
                    aria-selected={isSelected}
                    className={`faculty-pill-btn ${isSelected ? 'active' : ''}`}
                    onClick={() => {
                      setSearchParams({
                        w: paramWilaya || 'all',
                        f: f.slug,
                      });
                    }}
                  >
                    {fName}
                  </button>
                );
              })}
            </div>
          )}

          {/* Year Cards for selected faculty */}
          {selectedFaculty && (
            <div className="cards-grid">
              {getYearsForFaculty(selectedFaculty).map((dept) => {
                const yearLabel = t.years[dept];
                // Check if any subjects in this year have questions for this wilaya
                const yearSubjects = subjects.filter(
                  (s) => s.faculty_id === selectedFaculty.id && s.department === dept
                );
                let totalYearQuestions = 0;
                for (const sub of yearSubjects) {
                  totalYearQuestions += getSubjectCount(sub.id, paramWilaya || 'all');
                }
                const isDisabled = totalYearQuestions === 0;

                return (
                  <button
                    key={dept}
                    type="button"
                    className="wizard-card"
                    disabled={isDisabled}
                    onClick={() => handleSelectLevel(selectedFaculty, dept)}
                  >
                    <div className="card-top">
                      <div>
                        <h2 className="card-main-title">{yearLabel}</h2>
                        <p className="card-sub-info">
                          {facultyName}
                        </p>
                      </div>
                      <span className="card-badge">
                        {totalYearQuestions} {t.questionsCount}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </section>
      )}

      {/* =======================================================
          STEP 3: SUBJECT
          ======================================================= */}
      {currentStep === 3 && (
        <section aria-labelledby="step-3-title">
          <div className="step-header">
            <p className="step-indicator">3 / 4</p>
            <h1 id="step-3-title" className="step-title">
              {t.selectSubject}
            </h1>
            <p className="step-desc">{t.selectSubjectDesc}</p>
          </div>

          {filteredSubjects.length === 0 ? (
            <div className="state-box">
              <h2 className="state-title">{t.noSubjectsFound}</h2>
              <p className="state-desc">{t.noLessonsFound}</p>
              <button
                type="button"
                className="btn-ghost"
                onClick={() => handleJumpToStep(2)}
              >
                {t.changeStep}
              </button>
            </div>
          ) : (
            <div className="cards-grid">
              {filteredSubjects.map((sub) => {
                const count = getSubjectCount(sub.id, paramWilaya || 'all');
                const isDisabled = count === 0;

                return (
                  <button
                    key={sub.id}
                    type="button"
                    className="wizard-card"
                    disabled={isDisabled}
                    onClick={() => handleSelectSubject(sub.id)}
                  >
                    <div className="card-top">
                      <div>
                        <h2 className="card-main-title">{sub.name}</h2>
                        <p className="card-sub-info">{yearName}</p>
                      </div>
                      <span className="card-badge">
                        {count} {t.questionsCount}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </section>
      )}

      {/* =======================================================
          STEP 4: LESSON
          ======================================================= */}
      {currentStep === 4 && selectedSubject && (
        <section aria-labelledby="step-4-title">
          <div className="step-header">
            <p className="step-indicator">4 / 4</p>
            <div className="step-title-with-years">
              <h1 id="step-4-title" className="step-title">
                {t.selectLesson}
              </h1>
              {availableYears.length > 0 && (
                <div className="step-available-years-chips" aria-label={t.examYearsLabel}>
                  <span className="years-prefix-label">{t.examYearsLabel}</span>
                  {availableYears.map((year) => (
                    <span key={year} className="year-pill-badge">
                      {year}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <p className="step-desc">{t.selectLessonDesc}</p>
          </div>

          <div className="cards-grid">
            {/* Card 1: All Questions for this Subject */}
            {(() => {
              const allSubjectCount = getSubjectCount(selectedSubject.id, paramWilaya || 'all');
              const isDisabled = allSubjectCount === 0;

              return (
                <button
                  type="button"
                  className="wizard-card"
                  disabled={isDisabled}
                  onClick={() => handleOpenQuiz('all', false)}
                  style={{ borderInlineStart: '4px solid var(--ink)' }}
                >
                  <div className="card-top">
                    <div>
                      <h2 className="card-main-title">{t.allQuestionsOfSubject}</h2>
                      <p className="card-sub-info">{selectedSubject.name}</p>
                    </div>
                    <span className="card-badge">
                      {allSubjectCount} {t.allBadge}
                    </span>
                  </div>
                </button>
              );
            })()}

            {/* Individual Lessons */}
            {subjectLessons.map((lesson) => {
              const count = getLessonCount(lesson.id, paramWilaya || 'all');
              const isDisabled = count === 0;

              return (
                <div key={lesson.id} className="wizard-card">
                  <div
                    className="card-top"
                    style={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }}
                    onClick={() => {
                      if (!isDisabled) {
                        if (lesson.has_theory) {
                          setModalLesson(lesson);
                        } else {
                          handleOpenQuiz(lesson.id, false);
                        }
                      }
                    }}
                  >
                    <div>
                      <h2 className="card-main-title" dir="auto">
                        {lesson.name}
                      </h2>
                      <p className="card-sub-info">
                        {count} {count === 1 ? t.questionSingular : t.questionsCount}
                      </p>
                    </div>
                    <span className="card-badge">{count}</span>
                  </div>

                  {lesson.has_theory && (
                    <div className="card-footer-actions">
                      <button
                        type="button"
                        className="theory-quick-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/lesson/${lesson.id}?w=${encodeURIComponent(paramWilaya || 'all')}`);
                        }}
                        title={t.theoryTooltip}
                      >
                        <BookOpen size={14} />
                        <span>{t.theoryTitle}</span>
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Lesson Mode Choice Modal (Theory Lecture vs Exam Practice) */}
      {modalLesson && (
        <LessonModeModal
          lesson={modalLesson}
          questionCount={getLessonCount(modalLesson.id, paramWilaya || 'all')}
          wilaya={paramWilaya || 'all'}
          onSelectTheory={() => {
            const lid = modalLesson.id;
            setModalLesson(null);
            navigate(`/lesson/${lid}?w=${encodeURIComponent(paramWilaya || 'all')}`);
          }}
          onSelectQuiz={() => {
            const lid = modalLesson.id;
            setModalLesson(null);
            handleOpenQuiz(lid, false);
          }}
          onClose={() => setModalLesson(null)}
        />
      )}
    </main>
  );
};
